import { useEffect, useRef, useState } from "react";
import { Lock, X } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import ParticipantPicker from "@/components/ParticipantPicker";
import { useTribeMembers } from "@/hooks/useTribeMembers";
import { useChildren } from "@/hooks/useChildren";
import { useT } from "@/lib/t-wrapper";
import type { ParticipantRef } from "@/types/participant";

// ── Emoji options ─────────────────────────────────────────────────────────────

const COLLECTION_EMOJIS = [
  "🌟", "🎯", "🏠", "🌈", "🎨", "📸", "🎭", "🎪",
  "🌻", "🦋", "🌊", "🏖️", "🌙", "☀️", "🎄", "🎂",
  "🐾", "🌺", "🍃", "✨", "💫", "🔥", "🎵", "🎮",
  "📚", "🧩", "🚀", "🎈", "🌍", "💡", "🐿️", "🦔",
];

type SaveStatus = "idle" | "saving" | "saved" | "error";

// ── Props ─────────────────────────────────────────────────────────────────────

interface CollectionEditSheetProps {
  collectionId: string;
  name: string;
  icon: string;
  color: string;
  participantConnectionIds: string[];
  participantChildIds: string[];
  /** Children permanently locked — no toggle, shows 🔒 */
  lockedChildIds?: string[];
  /** Member IDs permanently locked — owner shows 🔒 when their id is listed */
  lockedMemberIds?: string[];
  isOpen: boolean;
  onClose: () => void;
  onSaved: (updated: {
    name: string;
    icon: string;
    participantConnectionIds: string[];
    participantChildIds: string[];
  }) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function CollectionEditSheet({
  collectionId,
  name,
  icon,
  color,
  participantConnectionIds,
  participantChildIds,
  lockedChildIds = [],
  lockedMemberIds = [],
  isOpen,
  onClose,
  onSaved,
}: CollectionEditSheetProps) {
  const { t } = useT();
  const { owner, coparents, contributors, isLoading: tribeLoading } = useTribeMembers();
  // Subjects (children + pets + later toys) — collections can be scoped to
  // any subject via the legacy-named participant_child_ids column.
  const { subjects: children } = useChildren();

  const [localName, setLocalName] = useState(name);
  const [localIcon, setLocalIcon] = useState(icon);
  const [localParticipants, setLocalParticipants] = useState<ParticipantRef[]>([]);
  const [localChildIds, setLocalChildIds] = useState<string[]>([]);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const initialized = useRef(false);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset field state when the sheet opens or switches collection
  useEffect(() => {
    if (!isOpen) {
      initialized.current = false;
      return;
    }
    setLocalName(name);
    setLocalIcon(icon);
    setLocalChildIds(participantChildIds);
    setSaveStatus("idle");
  }, [isOpen, collectionId, name, icon, participantChildIds]);

  // Initialize adult participants once tribe data is available.
  // Owner is always shown as selected (the collection is always "about" the creator).
  useEffect(() => {
    if (!isOpen || tribeLoading || initialized.current) return;

    const ownerRef: ParticipantRef | null = owner
      ? { kind: "owner", profileId: owner.profileId }
      : null;

    const connectionRefs: ParticipantRef[] = [...coparents, ...contributors]
      .filter((m) => participantConnectionIds.includes(m.connectionId))
      .map((m) => ({
        kind: "connection" as const,
        connectionId: m.connectionId,
        profileId: m.profileId,
        role: m.role,
        name: m.name,
      }));

    setLocalParticipants([
      ...(ownerRef ? [ownerRef] : []),
      ...connectionRefs,
    ]);
    initialized.current = true;
  }, [isOpen, tribeLoading, owner, coparents, contributors, participantConnectionIds]);

  useEffect(
    () => () => { if (savedTimer.current) clearTimeout(savedTimer.current); },
    [],
  );

  // Ensure the current icon is always in the picker list
  const iconList = Array.from(new Set([icon, ...COLLECTION_EMOJIS]));

  const saveButtonLabel = () => {
    if (saveStatus === "saving") return t("collections.edit.saving");
    if (saveStatus === "saved") return t("collections.edit.saved");
    if (saveStatus === "error") return t("collections.edit.error");
    return t("collections.edit.save");
  };

  const handleSave = async () => {
    if (!localName.trim()) return;
    setSaveStatus("saving");

    // Owner ref is informational only — only connection IDs are persisted
    const newConnectionIds = localParticipants
      .filter((p): p is Extract<ParticipantRef, { kind: "connection" }> => p.kind === "connection")
      .map((p) => p.connectionId);

    try {
      const { error } = await supabase
        .from("collections")
        .update({
          name: localName.trim(),
          icon: localIcon,
          participant_member_ids: newConnectionIds,
          participant_child_ids: localChildIds,
        })
        .eq("id", collectionId);

      if (error) throw error;

      setSaveStatus("saved");
      onSaved({
        name: localName.trim(),
        icon: localIcon,
        participantConnectionIds: newConnectionIds,
        participantChildIds: localChildIds,
      });

      savedTimer.current = setTimeout(() => {
        onClose();
      }, 800);
    } catch {
      setSaveStatus("error");
      toast.error(t("collections.edit.saveError"));
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="bottom"
        className="rounded-t-3xl px-6 pt-6 pb-10 max-h-[85dvh] overflow-y-auto"
        hideCloseButton
      >
        <SheetTitle className="sr-only">{t("collections.edit.title")}</SheetTitle>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-base font-semibold text-foreground">{t("collections.edit.title")}</p>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label={t("collections.edit.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-5">
          {/* Icon picker */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              {t("collections.edit.icon")}
            </p>
            <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 -mx-1 px-1">
              {iconList.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => { setLocalIcon(e); setSaveStatus("idle"); }}
                  className={`shrink-0 h-10 w-10 rounded-xl text-xl flex items-center justify-center transition-all ${
                    localIcon === e ? "bg-muted" : "hover:bg-muted"
                  }`}
                  style={
                    localIcon === e
                      ? { boxShadow: `0 0 0 2px ${color}`, outline: "none" }
                      : undefined
                  }
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground block mb-2">
              {t("collections.edit.name")}
            </label>
            <input
              type="text"
              value={localName}
              onChange={(e) => { setLocalName(e.target.value); setSaveStatus("idle"); }}
              placeholder={t("collections.edit.namePlaceholder")}
              className="w-full rounded-xl border border-border/60 bg-card px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50"
            />
          </div>

          {/* Who is this collection about — unified picker (children + ME + tribe) */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              {t("collections.edit.whoIsThisAbout")}
              {lockedMemberIds.length > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 normal-case text-[10px] font-medium">
                  <Lock className="h-2.5 w-2.5" /> {t("collections.edit.creatorLocked")}
                </span>
              )}
            </p>
            <ParticipantPicker
              mode="memory"
              value={localParticipants}
              onChange={(next) => { setLocalParticipants(next); setSaveStatus("idle"); }}
              accentColor={color}
              taggableSubjects={children}
              selectedSubjectIds={localChildIds}
              onSubjectIdsChange={(next) => { setLocalChildIds(next); setSaveStatus("idle"); }}
              lockedSubjectIds={lockedChildIds}
              lockedSubjectTooltip={(name) => t("collections.edit.lockedChildTooltip", { name })}
            />
          </div>
        </div>

        {/* Save button */}
        <div className="mt-6">
          <Button
            onClick={handleSave}
            disabled={!localName.trim() || saveStatus === "saving" || saveStatus === "saved"}
            className="w-full h-11 text-white"
            style={{
              backgroundColor:
                saveStatus === "saved"
                  ? "#22C55E"
                  : saveStatus === "error"
                  ? "#EF4444"
                  : color,
            }}
          >
            {saveButtonLabel()}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
